import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import useCurrentUser from 'src/hooks/use-current-user';
import { useSignatureValidation } from 'src/hooks/use-signature-validation';
import {
  resetSigningPinActionState,
  resetSigningPinRequest,
  setupSigningPinRequest,
} from 'src/redux/actions/signing-pin-action';
import PinForm from './pin-form';
import SignatureRender from './signature-render';
import { mapResetError, signingPinErrorText } from './signing-pin-errors';

type Mode = 'view' | 'setup' | 'reset';

//  THIN-7681 profile entry (secondary to first-time-on-sign): view the HR-rendered
//  signature and set up / reset the signing PIN.
//
//  APIT-656: resetting now takes the account password first — the PIN is the signing
//  credential, so an open session alone must not be enough to replace it. Setup is unchanged;
//  there is no existing PIN to protect the first time round.
export default function SignaturePinDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dispatch = useDispatch();
  const { hasPin, statusLoading } = useSignatureValidation();
  const [
    firstName,
    lastName,
    licenseNumber,
    credentials,
    //  APIT-655: the therapist's display overrides for the signature block. Null
    //  for everyone today, which renders exactly as it did before.
    signatureLineOverride,
    credentialsLineOverride,
  ] = useCurrentUser([
    'firstName',
    'lastName',
    'licenseNumber',
    'credentials',
    'signatureLineOverride',
    'credentialsLineOverride',
  ]) as Array<string | null>;
  const signingPin = useSelector((state: any) => state.signingPinReducer);
  const [mode, setMode] = useState<Mode>('view');

  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  //  APIT-656: one `resetError` now covers two fields — split it so a wrong password shows under
  //  the password box and a bad PIN under the PIN boxes. Memoised so PinForm's "clear the password
  //  on rejection" effect keys off a stable value rather than a fresh object every render.
  const { passwordError, pinError, lockedOut } = useMemo(
    () => mapResetError(signingPin?.resetError),
    [signingPin?.resetError]
  );

  useEffect(() => {
    if (open) {
      setMode('view');
      dispatch(resetSigningPinActionState());
    }
  }, [open, dispatch]);

  //  APIT-656: transient setup/reset errors are keyed to the form that produced them, so leaving
  //  or re-entering a mode must not resurrect one — otherwise a second visit to "Reset PIN" opens
  //  pre-filled with the previous attempt's "Current password is incorrect." (or a stale lockout,
  //  which would disable submit for a user who is no longer locked out).
  useEffect(() => {
    dispatch(resetSigningPinActionState());
  }, [mode, dispatch]);

  useEffect(() => {
    if (signingPin?.setupSuccess) {
      toast.success('Signing PIN created');
      setMode('view');
      dispatch(resetSigningPinActionState());
    }
  }, [signingPin?.setupSuccess, dispatch]);

  useEffect(() => {
    if (signingPin?.resetSuccess) {
      toast.success('Signing PIN reset');
      setMode('view');
      dispatch(resetSigningPinActionState());
    }
  }, [signingPin?.resetSuccess, dispatch]);

  return (
    <BootstrapDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Signature &amp; signing PIN
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Your signature comes from HR records; your PIN can be reset any time.
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Your signature
            </Typography>
            <SignatureRender
              name={fullName}
              license={licenseNumber}
              credentials={credentials}
              signatureLineOverride={signatureLineOverride}
              credentialsLineOverride={credentialsLineOverride}
              showBadge
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Rendered from your name exactly as it appears in HR and licensure records, so the
              signed name always matches your license. If your name changes, update it through HR —
              the signature follows automatically.
            </Typography>
          </Box>

          <Box>
            <Typography variant="overline" color="text.secondary">
              Signing PIN
            </Typography>

            {mode === 'view' && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mt: 1 }}
              >
                {statusLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Checking&hellip;
                  </Typography>
                ) : hasPin ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ letterSpacing: 4, fontSize: 20 }}>&bull;&bull;&bull;&bull;</Typography>
                    <Chip size="small" color="success" label="SET" />
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No PIN set yet.
                  </Typography>
                )}
                <Button variant="outlined" onClick={() => setMode(hasPin ? 'reset' : 'setup')}>
                  {hasPin ? 'Reset PIN' : 'Set up PIN'}
                </Button>
              </Stack>
            )}

            {mode === 'setup' && (
              <Box sx={{ mt: 2 }}>
                <PinForm
                  submitLabel="Save new PIN"
                  loading={signingPin?.setupLoading}
                  errorText={signingPinErrorText(signingPin?.setupError)}
                  warning="Write your PIN down and keep it somewhere safe. You'll need it every time you sign."
                  onSubmit={(pin) => dispatch(setupSigningPinRequest(pin))}
                  onCancel={() => setMode('view')}
                />
              </Box>
            )}

            {mode === 'reset' && (
              <Box sx={{ mt: 2 }}>
                <PinForm
                  submitLabel="Save new PIN"
                  loading={signingPin?.resetLoading}
                  requirePassword
                  passwordError={passwordError}
                  submitDisabled={lockedOut}
                  errorText={pinError}
                  onSubmit={(pin, password) =>
                    dispatch(resetSigningPinRequest(pin, password))
                  }
                  onCancel={() => setMode('view')}
                />
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </BootstrapDialog>
  );
}
